import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Input, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faLock } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { IssueReportsAPI } from '@share/api';
import {
  ISSUE_REPORT_ACCEPT,
  ISSUE_REPORT_MAX_COMMENT_LENGTH,
  ISSUE_REPORT_MAX_IMAGES,
} from '@share/common-types';
import { FunctionalRootState, Toaster } from '@share/utils';
import { capturePageScreenshot } from '../../utils/page-screenshot';
import { HasPermission, ReportIssuePermission } from '@utils';

import {
  IIssueReportImage,
  REPORT_ISSUE_BODY_CLASS,
  revokeImage,
  toIssueReportImage,
  useIssueReportContext,
  validateImages,
} from './utils';

import './style.scss';

const COMMENT_ROWS = 5;

export function ReportIssue() {
  const intl = useIntl();

  const { user, userWallet } = useSelector((state: FunctionalRootState) => state.loginStore);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [images, setImages] = useState<IIssueReportImage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const context = useIssueReportContext();

  // Visibility is driven by the REPORTISSUE permission claim. The endpoint
  // itself is open to any authenticated user, so this is presentation only.
  const canReportIssue = HasPermission(user ?? userWallet, ReportIssuePermission);

  // Advertise the trigger's presence so other bottom-left floating controls
  // only stack above it while it is actually on screen.
  useEffect(() => {
    if (!canReportIssue) {
      return undefined;
    }

    document.body.classList.add(REPORT_ISSUE_BODY_CLASS);

    return () => {
      document.body.classList.remove(REPORT_ISSUE_BODY_CLASS);
    };
  }, [canReportIssue]);

  // Object URLs outlive React state, so release whatever is still held on
  // unmount. A ref keeps the cleanup pointed at the latest list.
  const imagesRef = useRef<IIssueReportImage[]>([]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach(revokeImage);
    },
    [],
  );

  const resetForm = useCallback(() => {
    setComment('');
    imagesRef.current.forEach(revokeImage);
    setImages([]);
    setError(null);
  }, []);

  // The screenshot is taken before the modal mounts, otherwise the modal itself
  // is what ends up in the picture.
  const handleOpen = useCallback(async () => {
    resetForm();
    setCapturing(true);

    const screenshot = await capturePageScreenshot();

    setImages(screenshot ? [toIssueReportImage(screenshot, true)] : []);
    setCapturing(false);
    setIsOpen(true);
  }, [resetForm]);

  const handleCancel = useCallback(() => {
    if (loading) {
      return;
    }

    setIsOpen(false);
    resetForm();
  }, [loading, resetForm]);

  const handleAddImages = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files ?? []);

      if (!selected.length) {
        return;
      }

      const existing = images.map((image) => image.file);
      const { accepted, error: validationError } = validateImages(existing, selected, intl);

      // Keep the entries already on screen so their object URLs stay valid, and
      // wrap only the files validateImages newly let through.
      const added = accepted
        .filter((file) => !existing.includes(file))
        .map((file) => toIssueReportImage(file, false));

      setImages([...images, ...added]);
      setError(validationError);

      // Allows re-picking the same file after it was removed.
      event.target.value = '';
    },
    [images, intl],
  );

  const handleRemoveImage = useCallback(
    (index: number) => {
      const target = images[index];

      // The auto-captured screenshot is always sent and has no remove control.
      if (!target || target.isAuto) {
        return;
      }

      revokeImage(target);
      setImages(images.filter((_, i) => i !== index));
      setError(null);
    },
    [images],
  );

  const handleSubmit = useCallback(() => {
    if (isEmpty(comment.trim())) {
      setError(intl.formatMessage({ id: 'report.issue.comment.required' }));
      return;
    }

    setLoading(true);
    setError(null);

    IssueReportsAPI.create({
      context,
      comment: comment.trim(),
      images: images.map((image) => image.file),
    })
      .then(() => {
        Toaster.success(intl.formatMessage({ id: 'report.issue.success' }));
        setIsOpen(false);
        resetForm();
      })
      .catch((e) => {
        // The endpoint answers 400 with a plain-text validation message.
        const message =
          typeof e?.response?.data === 'string' && !isEmpty(e.response.data)
            ? e.response.data
            : intl.formatMessage({ id: 'report.issue.error' });

        console.error('Issue report failed', e);
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [comment, context, images, intl, resetForm]);

  if (!canReportIssue) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="report-issue__trigger"
        onClick={handleOpen}
        disabled={capturing}
        title={intl.formatMessage({ id: 'report.issue.button' })}
      >
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <span className="report-issue__trigger-label">
          <FormattedMessage id={capturing ? 'report.issue.capturing' : 'report.issue.button'} />
        </span>
      </button>

      <Modal
        open={isOpen}
        title={intl.formatMessage({ id: 'report.issue.title' })}
        className="report-issue__modal"
        onCancel={handleCancel}
        maskClosable={!loading}
        footer={null}
        width={560}
        centered
      >
        <div className="report-issue__content">
          <p className="report-issue__description">
            <FormattedMessage id="report.issue.description" />
          </p>

          <label className="report-issue__label" htmlFor="report-issue-comment">
            <FormattedMessage id="report.issue.comment.label" />
          </label>
          <Input.TextArea
            id="report-issue-comment"
            rows={COMMENT_ROWS}
            value={comment}
            disabled={loading}
            maxLength={ISSUE_REPORT_MAX_COMMENT_LENGTH}
            placeholder={intl.formatMessage({ id: 'report.issue.comment.placeholder' })}
            onChange={(event) => {
              setComment(event.target.value);
              setError(null);
            }}
          />

          <div className="report-issue__images">
            <label className="report-issue__label">
              <FormattedMessage id="report.issue.images.label" />
            </label>
            <p className="report-issue__hint">
              <FormattedMessage
                id="report.issue.images.hint"
                values={{ max: ISSUE_REPORT_MAX_IMAGES }}
              />
            </p>

            {images.length ? (
              <ul className="report-issue__image-list">
                {images.map((image, index) => (
                  <li key={image.url} className="report-issue__image-item">
                    <img
                      className="report-issue__image-thumb"
                      src={image.url}
                      alt={image.file.name}
                    />
                    <span className="report-issue__image-meta">
                      <span className="report-issue__image-name">
                        {image.isAuto ? (
                          <FormattedMessage id="report.issue.images.screenshot" />
                        ) : (
                          image.file.name
                        )}
                      </span>
                    </span>
                    {image.isAuto ? null : (
                      <button
                        type="button"
                        className="report-issue__image-remove"
                        disabled={loading}
                        onClick={() => handleRemoveImage(index)}
                      >
                        <FormattedMessage id="report.issue.images.remove" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="report-issue__file-input"
              accept={ISSUE_REPORT_ACCEPT}
              onChange={handleAddImages}
            />
            <Button
              className="report-issue__add-images"
              disabled={loading || images.length >= ISSUE_REPORT_MAX_IMAGES}
              onClick={() => fileInputRef.current?.click()}
            >
              <FormattedMessage id="report.issue.images.add" />
            </Button>
          </div>

          <p className="report-issue__privacy">
            <FontAwesomeIcon icon={faLock} />{' '}
            <FormattedMessage id="report.issue.privacy" />
          </p>

          {error ? <p className="report-issue__error">{error}</p> : null}

          <div className="report-issue__actions">
            <Button className="report-issue__cancel" disabled={loading} onClick={handleCancel}>
              <FormattedMessage id="report.issue.cancel" />
            </Button>
            <Button
              type="primary"
              className="report-issue__submit"
              loading={loading}
              onClick={handleSubmit}
            >
              <FormattedMessage id="report.issue.submit" />
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
